import { useContext, useMemo } from 'react';
import * as React from 'react';
import styled from 'styled-components';

import { color } from '@creditornot/cb-ingredients/design-tokens';

import { DataTableContext } from './DataTableContext';
import { DataTableSubRows } from './DataTableSubRows';
import { RowDragSelect } from './RowDragSelect';
import { RowMenuExpand } from './RowMenuExpand';
import {
  AlignHorizontal,
  AlignVertical,
  ColumnType,
  SelectMode,
  TRowModel,
  TableRowAction,
} from './types';
import {
  getAllSubRowsMatchRow,
  getGridTemplateColumns,
  getRowSelectionStatus,
  horizontalContentAlignmentMap,
  verticalContentAlignmentMap,
} from './utils';

const Column = styled.div<{
  $align?: AlignHorizontal;
  $alignVertical?: AlignVertical;
}>`
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  justify-content: ${({ $alignVertical }: { $alignVertical?: AlignVertical }) =>
    verticalContentAlignmentMap[$alignVertical || 'middle']};
  align-items: ${({ $align }: { $align?: AlignHorizontal }) =>
    horizontalContentAlignmentMap[$align || 'left']};

  &:first-child {
    padding-left: 24px;
  }
`;

const SubRows = styled.div<{ $gridTemplateColumns: string }>`
  grid-column: 1 / -1;
  display: grid;
  grid: inherit;
  grid-auto-flow: row;
  border-top: 1px solid ${color.border};

  .content-container {
    background: ${color.bgSurface};
  }

  @supports (grid-template-rows: subgrid) {
    grid-template-columns: subgrid;
  }

  @supports not (grid-template-rows: subgrid) {
    grid-template-columns: ${({ $gridTemplateColumns }) => $gridTemplateColumns};
    overflow-x: auto;
  }
`;

const TableRow = styled.div<{ $gridTemplateColumns: string; $isLastSubRow?: boolean }>`
  @supports (grid-template-rows: subgrid) {
    display: grid;
    grid: inherit;
    grid-column: 1 / -1;
    grid-template-columns: subgrid;
    border-bottom: ${({ $isLastSubRow }) => ($isLastSubRow ? 'none' : `1px solid ${color.border}`)};
  }

  @supports not (grid-template-rows: subgrid) {
    display: contents;
  }

  .content-container {
    background: ${color.bg};
  }

  &:hover .content-container {
    background: ${color.bgSurfaceSecondaryHovered};
  }

  &[aria-expanded='true'] .content-container {
    background: ${color.bgSurface};
  }

  &[aria-selected='true'] .content-container {
    background: ${color.bgSurfaceBrandSelected};
  }

  &[aria-selected='true']:hover .content-container {
    background: ${color.bgSurfaceBrandHovered};
  }
`;

type Props<Row extends TRowModel<Row>> = {
  rowIndex: number;
  record: Row;
  columns: ColumnType<Row>[];
  rowLeftSpacing?: number;
  parentRow?: Row;
  expandedRows?: Record<string, boolean>;
  renderSubComponent?: (record: Row) => React.ReactNode;
  getRowMenu?: (record: Row) => TableRowAction<Row>[];
  getHideSubRows?: (record: Row) => boolean;
  handleExpandedToggle?: (rowKey: string) => void;
  isLastSubRow?: boolean;
  isRowDisabled?: (record: Row) => boolean;
  disabledRowMessage?: string;
  hideSelection?: boolean;
  dataTestId?: string;
};

export const DataTableRow = <Row extends TRowModel<Row>>({
  rowIndex,
  record,
  columns = [],
  parentRow,
  expandedRows = {},
  renderSubComponent,
  getRowMenu,
  handleExpandedToggle,
  isLastSubRow = false,
  isRowDisabled,
  disabledRowMessage,
  hideSelection = false,
  dataTestId,
}: Props<Row>) => {
  const {
    selectionState,
    handleToggleRow,
    hasMenuExpandColumn = false,
    hasDragSelectColumn = false,
  } = useContext(DataTableContext);

  const selectionMode = useMemo(
    () => getRowSelectionStatus(selectionState, record, parentRow),
    [selectionState, record, parentRow],
  );

  const rowKey = record.id;
  const isExpanded = expandedRows[rowKey];
  const allSubRowsMatchRow = getAllSubRowsMatchRow(record);
  const subComponent = renderSubComponent?.(record);

  if (!columns.length) {
    return null;
  }

  return (
    <TableRow
      aria-expanded={isExpanded}
      aria-selected={selectionMode === 'ALL' || selectionMode === 'SOME'}
      role="row"
      data-test-id={dataTestId}
      $gridTemplateColumns={getGridTemplateColumns(
        hasDragSelectColumn,
        hasMenuExpandColumn,
        columns,
      )}
      $isLastSubRow={isLastSubRow}
    >
      <RowDragSelect
        selectionMode={selectionMode}
        hasDragSelectColumn={hasDragSelectColumn}
        handleToggleRow={(value: SelectMode) => {
          if (handleToggleRow) {
            handleToggleRow(value, record, parentRow);
          }
        }}
        isSelectDisabled={isRowDisabled?.(record)}
        disabledRowMessage={disabledRowMessage}
        hideSelection={hideSelection}
      />

      {columns.map((column) => {
        const { id, render, alignContent, alignContentVertical, className = '' } = column;

        return (
          <Column
            key={id}
            className={`content-container ${className}`}
            role="cell"
            $align={alignContent}
            $alignVertical={alignContentVertical}
          >
            {render({ record, rowIndex })}
          </Column>
        );
      })}
      <RowMenuExpand
        hasMenuExpandColumn={hasMenuExpandColumn || !!getRowMenu}
        getRowMenu={getRowMenu}
        isExpanded={isExpanded}
        handleExpandedToggle={handleExpandedToggle}
        record={record}
        hasSubComponent={Boolean(record.subRows?.length) || !!subComponent}
      />
      {expandedRows[record.id] && (
        <SubRows
          $gridTemplateColumns={getGridTemplateColumns(
            hasDragSelectColumn,
            hasMenuExpandColumn,
            columns,
          )}
          role="rowgroup"
        >
          {allSubRowsMatchRow && !subComponent && record.subRows ? (
            <DataTableSubRows
              dataSource={record.subRows as Row[]}
              parentRow={record}
              columns={columns}
              getRowMenu={getRowMenu}
            />
          ) : (
            (subComponent ?? null)
          )}
        </SubRows>
      )}
    </TableRow>
  );
};
